<script lang="ts" setup>
  import { useHttpClient, useRoute, useTranslator } from '@othercommerce/foundation';
  import { computed, type PropType } from 'vue';
  import { Features } from '@shared/features';
  import { type Dictionary, type CustomerType, CustomerType as CustomerTypeEnum, type MedicalType, type CountriesDictionary, type RegionsDictionary } from '@othercommerce/domain';
  import { SignUpFormControlsCompany, SignUpFormControlsMedical, SignUpFormControlsPersonal } from '@storefront/components';
  import { signUpEvent, useGoogleTagManager } from '@storefront/storefront';

  interface CustomerTypeDictionaryRecord {
    label: string;
    value: CustomerType;
  }

  interface MedicalTypeDictionaryRecord {
    label: string;
    value: MedicalType;
  }

  const props = defineProps({
    countries: { type: Array as PropType<CountriesDictionary>, required: true },
    customerTypes: { type: Array as PropType<Dictionary<CustomerTypeDictionaryRecord>>, required: true },
    registerAgencies: { type: Array as PropType<Dictionary>, required: false },
    medicalPracticeGroups: { type: Array as PropType<Dictionary>, required: false },
    medicalTypes: { type: Array as PropType<Dictionary<MedicalTypeDictionaryRecord>>, required: false },
    regions: { type: Object as PropType<RegionsDictionary>, required: true },
  });

  const route = useRoute();
  const http = useHttpClient();
  const gtm = useGoogleTagManager();
  const translator = useTranslator();

  const isRPWDLEnabled = Features.medicalContractorsRegistry.isEnabled;

  const termsAndConditions = computed(() => {
    return translator.trans('SHOP.AUTH.LABEL.TERMS_AND_CONDITIONS', { url: route('web.termsAndCondition.index') });
  });

  const privacyPolicy = computed(() => {
    return translator.trans('SHOP.AUTH.LABEL.PRIVACY_POLICY', { url: route('web.privacyPolicy.index') });
  });

  const boxChecked = computed(() => {
    return translator.trans('SHOP.COMMON.LABEL.REQUIRED_FIELDS');
  });

  const defaultCustomerType = function () {
    return props.customerTypes[0]?.value;
  };

  const defaultMedicalType = function () {
    if (props.medicalTypes) return props.medicalTypes[0]?.value;
    return null;
  };

  function selectedFormComponent(type: CustomerType) {
    if (type === CustomerTypeEnum.MEDICAL && isRPWDLEnabled) return SignUpFormControlsMedical;
    if (type === CustomerTypeEnum.PERSONAL) return SignUpFormControlsPersonal;
    if (type === CustomerTypeEnum.COMPANY) return SignUpFormControlsCompany;
  }

  function selectedFormComponentProps(type: CustomerType, data: any) {
    const common = { countries: props.countries, regions: props.regions };

    switch (type) {
      case CustomerTypeEnum.MEDICAL:
        return {
          data,
          registerAgencies: props.registerAgencies,
          medicalPracticeGroups: props.medicalPracticeGroups,
          medicalTypes: props.medicalTypes,
          countries: props.countries,
        };
      case CustomerTypeEnum.PERSONAL:
      case CustomerTypeEnum.COMPANY:
        return common;
      default:
        return {};
    }
  }

  function onSubmit(data: any) {
    return http.post(route('web.account.register.store', data)).then(() => gtm.push(signUpEvent()));
  }
</script>
<template>
  <FormController v-slot="{ data, processing }" :data="{ countryCode: 'PL', type: defaultCustomerType(), rpwdlMedicalType: defaultMedicalType()  }" class="sign-up-form" @submit="onSubmit">
    <div v-if="$slots.info" class="sign-up-form__info">
      <slot name="info" />
    </div>
    <div class="sign-up-form__form">
      <SfFormRadio :label="$t('SHOP.AUTH.LABEL.SIGN_UP_AS')" :options="customerTypes" name="type" required />
      <component :is="selectedFormComponent(data.type)" v-bind="selectedFormComponentProps(data.type, data)" />
    </div>
    <div class="sign-up-form__conditions">
      <SfFormSwitch name="acceptConditions" required>
        <span v-html="termsAndConditions" />
      </SfFormSwitch>
      <SfFormSwitch name="acceptRodo" required>
        <span v-html="privacyPolicy" />
      </SfFormSwitch>
      <SfFormSwitch name="acceptNewsletter">
        {{ $t('SHOP.AUTH.LABEL.SIGN_UP_NEWSLETTER') }}
      </SfFormSwitch>
    </div>
    <p class="sign-up-form__required-fields" v-html="boxChecked" />
    <SfButton :loading="processing" primary type="submit">
      {{ $t('SHOP.AUTH.ACTION.SIGN_UP') }}
    </SfButton>
  </FormController>
</template>

<style lang="scss">
  :root {
    --sf-sign-up-form-required-color: var(--sf-input-label-required-color);
    --sf-sign-up-form-required-font-size: 0.75rem;
    --sf-sign-up-form-conditions-link-color: var(--primary-color);
    --sf-sign-up-form-conditions-link-color-hover: var(--primary-color-hover);
    --sf-sign-up-form-conditions-trans-duration: 200ms;
  }

  .sign-up-form {
    &__form,
    &__conditions {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__form {
      gap: var(--form-gap-y) var(--form-gap-x);
    }

    &__conditions a {
      text-decoration: underline;
      text-decoration-color: transparent;
      transition: text-decoration-color 0.2s ease-out;
    }

    @media(hover: hover) {
      a:hover {
        text-decoration-color: var(--primary-color);
      }
    }

    &__required-fields {
      font-size: var(--sf-sign-up-form-required-font-size);

      b {
        color: var(--sf-sign-up-form-required-color);
      }
    }
  }
</style>
